// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { ResponsiveBar } from '@nivo/bar';
import { numberToFixed } from '@shared/ui-kit/utils';
import { ChartContainer } from '@shared/ui-kit/components';
import { type FC, useMemo } from 'react';
import { type TrackingExpected } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { useTranslation } from 'react-i18next';

export interface TrackingErrorProps {
  tracking_expected: TrackingExpected;
}

export const TrackingError: FC<TrackingErrorProps> = ({ tracking_expected }) => {
  const { t } = useTranslation();
  const { tracking_error, probability } = tracking_expected;

  const data = useMemo(
    () =>
      tracking_error.map((error, idx) => ({
        trackingError: numberToFixed(error * 100, 1),
        probability: numberToFixed(probability[idx] * 100, 2),
      })),
    [tracking_error, probability],
  );

  const trackingErrorValue = useMemo(
    () => (
      <div className="flex text-lg font-bold ">
        <h3 className="mr-2">
          {t('glossary.tracking-error', { ns: 'glossary' })}:{' '}
        </h3>
        <div className="!money-display text-primary">
          {numberToFixed(
            tracking_expected.expected_tracking_kpis.tracking_error * 100,
          )}{' '}
          %
        </div>
      </div>
    ),
    [tracking_expected.expected_tracking_kpis.tracking_error, t],
  );

  return (
    <ChartContainer
      header={t('common.tracking-error-probability', { ns: 'common' })}
      popupName="charts.trackingError"
      headerRight={trackingErrorValue}
      tooltip
    >
      <ResponsiveBar
        data={data}
        keys={['probability']}
        indexBy="trackingError"
        margin={{ top: 50, right: 60, bottom: 50, left: 60 }}
        padding={0.2}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={'rgba(16, 114, 186, 0.75)'}
        borderRadius={8}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 16,
          tickRotation: -45,
          legend: '',
          legendPosition: 'middle',
          legendOffset: 32,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          legend: '',
          legendPosition: 'middle',
          legendOffset: -46,
        }}
        enableGridY={false}
        enableLabel={false}
        labelSkipHeight={12}
        role="application"
        theme={{
          axis: {
            ticks: {
              text: {
                fontSize: 11,
                fontFamily: 'poppins',
                fill: 'var(--blueGrayMuted)',
              },
            },
          },
        }}
        animate={false}
      />
    </ChartContainer>
  );
};
